import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

import authService from "../api/auth";
import { getCsrfToken } from "../utils/csrf";

// based on Allauth API
interface User {
  id: number;
  display: string;
  email: string;
  has_usable_password: boolean;
  initials?: string;
  is_daily_message_paused?: boolean;
}

interface AuthContextState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{
    success: boolean;
    error?: AuthError;
  }>;
  logout: () => Promise<void>;
  register: (data: any) => Promise<{
    success: boolean;
    error?: AuthError;
  }>;
  checkAuth: () => Promise<boolean>;
  resendVerification: (email: string) => Promise<{
    success: boolean;
    error?: AuthError;
  }>;
  updateUser: (updates: Partial<User>) => Promise<{
    success: boolean;
    error?: AuthError;
  }>;
}

interface AuthError {
  message: string;
  code?: string;
  detail?: string;
  data?: any;
}

const AuthContext = createContext<AuthContextState>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => ({ success: false }),
  logout: async () => {},
  register: async () => ({ success: false }),
  checkAuth: async () => false,
  resendVerification: async () => ({ success: false }),
  updateUser: async () => ({ success: false }),
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [hasInitialCheck, setHasInitialCheck] = useState(false);

  useEffect(() => {
    // Only do the initial auth check once when the provider mounts
    if (!hasInitialCheck) {
      checkAuth();
    }
  }, []);

  const checkAuth = async (): Promise<boolean> => {
    // avoid concurrent auth checks
    if (isCheckingAuth) {
      return false;
    }

    setIsCheckingAuth(true);
    setIsLoading(true);

    try {
      const response = await authService.getSession();

      if (response.user) {
        const userData: User = {
          id: response.user.id ?? 0,
          email: response.user.email,
          display: response.user.email,
          has_usable_password: response.user.has_usable_password ?? true,
          initials: response.user.email.substring(0, 1).toUpperCase()
        };

        // Try to fetch extended user data from our User API using the new "me" endpoint
        try {
          const response = await fetch('/api/users/me/', {
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const currentUser = await response.json();
            userData.is_daily_message_paused = currentUser.is_daily_message_paused;
          }
        } catch (error) {
          console.warn('Failed to fetch extended user data:', error);
          // Continue with basic user data
        }

        setUser(userData);
        setIsAuthenticated(true);
        setHasInitialCheck(true);
        return true;
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setHasInitialCheck(true);
        return false;
      }
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      setHasInitialCheck(true);
      return false;
    } finally {
      setIsLoading(false);
      setIsCheckingAuth(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const result = await authService.login(email, password);
      if (result.success && result.data) {
        await new Promise(resolve => setTimeout(resolve, 100));

        const isAuthenticated = await checkAuth();

        if (!isAuthenticated) {
          await new Promise(resolve => setTimeout(resolve, 500));
          const secondCheck = await checkAuth();

          if (!secondCheck) {
            return {
              success: false,
              error: {
                message: "Authentication failed after login. Session may not be valid."
              }
            };
          }
        }

        return { success: true };
      }

      return {
        success: false,
        error: result.error
      };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || 'Unknown error occurred',
        }
      };
    }
  };

  const logout = async () => {
    try {
      const result = await authService.logout();
      if (result.success) {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
    }
  };

  // new user reg
  const register = async (data: any) => {
    try {
      const result = await authService.register(data);
      if (result.success) {
        await checkAuth();
        return { success: true };
      }
      return {
        success: false,
        error: result.error
      };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || 'Unknown error occurred',
        }
      };
    }
  };

  const resendVerification = async (email: string) => {
    try {
      const result = await authService.resendVerification(email);
      return {
        success: result.success,
        error: result.error
      };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || 'Failed to resend verification email',
        }
      };
    }
  };

  const updateUser = async (updates: Partial<User>) => {
    try {
      if (!user) {
        return {
          success: false,
          error: { message: "No user logged in" },
        };
      }

      // Get CSRF token using shared utility
      const csrfToken = await getCsrfToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      if (csrfToken) {
        headers["X-CSRFToken"] = csrfToken;
      }

      // Use the new "me" endpoint to update current user's settings
      const response = await fetch("/api/users/me/", {
        method: "PATCH",
        credentials: "include",
        headers,
        body: JSON.stringify(updates),
      });

      if (response.ok) {
        const result = await response.json();
        // Update the local user state with the returned user data
        const updatedUser = { ...user, ...result };
        setUser(updatedUser);
        return { success: true };
      }

      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: {
          message: errorData.message || "Failed to update user settings",
        },
      };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update user settings",
        },
      };
    }
  };

  const value: AuthContextState = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    register,
    checkAuth,
    resendVerification,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;

